const sessionStorageKey = 'tilesetUrl';
window.setTilesetUrl = (url?: string) => {
    if (url === undefined || url === null || url === '') {
        sessionStorage.removeItem(sessionStorageKey);
        console.warn('TilesetUrl has been unset.');
    }
    else {
        if (!url.endsWith('/')) {
            console.error('TilesetUrls must point towards a folder containing a tileset.json file and have a trailing slash.');
        }
        
        sessionStorage.setItem(sessionStorageKey, url);
        console.warn(`TilesetUrl has been set to '${url}'. This url will be used until the browser is restarted. Call this function without an url to use the default value.`);
    }
}
window.getTilesetUrl = (): string => {
    const sessionStorageUrl = sessionStorage.getItem(sessionStorageKey);
    
    if (sessionStorageUrl === null) {
        return window.tilesetUrl ?? 'https://3d-tiles.solarprotool.com/v4/';
    }
    else {
        return sessionStorageUrl;
    }
}
const tilesetLimitSessionStorageKey = 'tilesetLimit';
window.setTilesetLimit = (limit: number) => {
    if (limit === undefined || limit === null) {
        sessionStorage.removeItem(tilesetLimitSessionStorageKey);
        console.warn('TilesetLimit has been unset.');
    }
    else {
        sessionStorage.setItem(tilesetLimitSessionStorageKey, limit + '');
        console.warn(`TilesetLimit has been set to ${limit}. This limit will be used until the browser restarts.`);
    }
}
window.getTilesetLimit = () => {
    const sessionStorageLimit = sessionStorage.getItem(tilesetLimitSessionStorageKey);
    if (sessionStorageLimit === null) {
        return 150;
    }
    else {
        return parseInt(sessionStorageLimit);
    }
}

module MapDrawing {
    export class Gis3DBuildings {
        private tilesetLoader?: TilesetLoader = undefined;
        private cleanupFn: (() => void)[] = [];
        public borderJson: { lat: number, lng: number }[][][] = null;

        public async downloadBorderJson(): Promise<any> {
            const tilesetUrl: string = window.getTilesetUrl();
            
            if (this.borderJson) {
                return this.borderJson;
            }
            return fetch(`${tilesetUrl}borders.json.gz?${this.preventCaching()}`)
                .then(response => response.json())
                .then(borders => {
                    this.borderJson = borders;
                    return borders; // Return the value
                })
                .catch((error: unknown) => {
                    console.error('3D-Buildings borders file could not be fetched', error);
                });
        }
        public start(mapDrawer: MapDrawer, onBuildingClicked: (mapShapes: SolarProTool.MapShape[]) => void, onBuildingClickStart?: () => void) {
            const map = mapDrawer.map;

            const [credit, creditCleanup] = this.createCredits(map);
            this.cleanupFn.push(creditCleanup);

            const borderCleanup = this.createBorders(window.getTilesetUrl(), map);
            this.cleanupFn.push(borderCleanup);

            this.tilesetLoader = this.createTilesetLoader(window.getTilesetUrl(), credit, map, onBuildingClicked, onBuildingClickStart);

            const updateFunction = () => {
                const zoom = map.getZoom();

                if (zoom > 15) {
                    if (this.tilesetLoader === undefined) {
                        this.tilesetLoader = this.createTilesetLoader(window.getTilesetUrl(), credit, map, onBuildingClicked, onBuildingClickStart);
                    }

                    const levasoftView = this.getLevasoftView(map);
                    if (levasoftView) {
                        this.tilesetLoader.updateView(this.getLoadersGLView(map), levasoftView);
                    }
                }
                else {
                    this.tilesetLoader?.close();
                    this.tilesetLoader = undefined;
                }
            }

            const listener2 = map.addListener('bounds_changed', updateFunction);
            this.cleanupFn.push(() => listener2.remove());
            updateFunction();
        }

        public stop() {
            this.cleanupFn.forEach(fn => fn());
            this.cleanupFn = [];
            this.tilesetLoader?.close();
        }
        
        private createTilesetLoader(tilesetUrl: string, creditElement: HTMLDivElement, map: google.maps.Map, onBuildingClicked: (mapShapes: SolarProTool.MapShape[]) => void, onBuildingClickStart?: () => void) {
            let creditList: Record<string, string | undefined> | null = null;
            let clickSeq = 0;
            const tilesetLoader = new window.TilesetLoader(
                `${tilesetUrl}tileset.json?${this.preventCaching()}`,
                this.attachCredits(
                    creditElement,
                    gltf => {
                        return this.addBuildings(gltf, map, building => {
                            const mySeq = ++clickSeq;
                            onBuildingClickStart?.();
                            this.fetchCompressedMapShapes(tilesetUrl, building)
                                .then(mapShapes => {
                                    if (mySeq === clickSeq) {
                                        // Guarantee a stable IdString on the building shape so the
                                        // re-click early-return in _startBuildings works even when
                                        // the server omits IdString for the building polygon.
                                        const buildingId = building.id_uuid ?? building.id ?? building.id_text;
                                        if (buildingId) {
                                            const bTypeName = (window as any).BuildingTypeName;
                                            mapShapes.forEach((s: any) => {
                                                if (s.TypeName === bTypeName && !s.IdString) {
                                                    s.IdString = buildingId;
                                                }
                                            });
                                        }
                                        onBuildingClicked(mapShapes);
                                    }
                                })
                        })
                    },
                    () => creditList ?? (() => { console.error('3DBuilding: Credits have failed to initialize.'); return { } })()
                ),
                credits => creditList = credits
            );

            return tilesetLoader;
        }
        
        private createCredits(map: google.maps.Map) {
            const credit = document.createElement('div');
            credit.style.backgroundColor = 'rgba(245,245, 245, 0.7)';
            credit.style.color = 'black';
            credit.style.paddingTop = '2px';
            const mvc = map.controls[google.maps.ControlPosition.BOTTOM_CENTER];
            mvc.push(credit);
            const removeCredit = () => {
                const index = mvc.getArray().indexOf(credit);
                if (index !== -1) {
                    mvc.removeAt(index);   
                }
            }
            return [credit, removeCredit] as const;
        }

        private attachCredits(creditElement: HTMLDivElement, tileEffect: TileEffect, credits: () => Record<string, string | undefined>): TileEffect {
            const sourceCounter = new SourceCounter(credits);

            return gltf => {
                const tileSources = [...new Set<string>(gltf.scene.children[0].children.map(mesh => mesh.userData.source)).values()]; 
                sourceCounter.add(tileSources);
                
                this.updateCreditComponent(creditElement, sourceCounter.getActiveCredits());
                const cleanUp = tileEffect(gltf);
                return () => {
                    sourceCounter.substract(tileSources);
                    cleanUp();
                    this.updateCreditComponent(creditElement, sourceCounter.getActiveCredits());
                };
            }
        }
        
        private updateCreditComponent(creditElement: HTMLDivElement, credits: string[]) {
            const nodes: HTMLParagraphElement[] = [];

            for (const credit of credits) {
                const p = document.createElement('p');
                p.style.marginBottom = '0';
                p.style.padding = '0 5px';
                p.innerHTML = credit;
                nodes.push(p);
            }

            (creditElement as any).replaceChildren(...nodes);
        }
        
        private createBorders(tilesetUrl: string, map: google.maps.Map) {
            const controller = new AbortController();
            let borderPolygonList: google.maps.Polygon[] = [];
            // clickable: false — the grey no-data overlay must not swallow clicks,
            // otherwise the map 'click' listener (out-of-coverage dialog) never fires.
            if (this.borderJson) {
                for (const border of this.borderJson) {
                    borderPolygonList.push(new google.maps.Polygon({
                        paths: border,
                        geodesic: false,
                        fillOpacity: 0.7,
                        fillColor: '#8E8E8E',
                        strokeWeight: 0,
                        clickable: false,
                        map: map
                    }));
                }
            } else {
                this.downloadBorderJson().then((borderJson) => {
                    if (!borderJson) return;
                    for (const border of borderJson) {
                        borderPolygonList.push(new google.maps.Polygon({
                            paths: border,
                            geodesic: false,
                            fillOpacity: 0.7,
                            fillColor: '#8E8E8E',
                            strokeWeight: 0,
                            clickable: false,
                            map: map
                        }));
                    }
                });
            }

            return () => {
                controller.abort();
                borderPolygonList.forEach(border => border.setMap(null));
            }
        }

        private getLoadersGLView(map: google.maps.Map) {
            const div = map.getDiv();
            const center = map.getCenter();

            const view: LoadersGLView = {
                width: div.clientWidth,
                height: div.clientHeight,
                latitude: center?.lat(),
                longitude: center?.lng(),
                zoom: map.getZoom()
            };
            return view;
        }
        
        private getLevasoftView(map: google.maps.Map) {
            const bounds = map.getBounds();
			if (!bounds) return null;
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            
            const view: LevasoftView = {
                north: ne.lat(),
                east: ne.lng(),
                south: sw.lat(),
                west: sw.lng()
            };
            return view;
        }

        private async fetchCompressedMapShapes(tilesetUrl: string, building: Building): Promise<SolarProTool.MapShape[]> {
            const url = `${tilesetUrl}${building.tile}`;
            console.log(`3D-Building from dataset '${building.source}' with id ${building.id}[${building.id_number}, ${building.id_uuid}, ${building.id_text}] in tile '${url}' with ${building.polygon_count} polygons. date_imported ${building.date_imported} and date_improved ${building.date_improved}.`);
            
            try {
                const response = await fetch(url);
                const json = await response.json();
                const result = json[building.id];
                return result;
            }
            catch(error) {
                console.error(error);
                return [];
            }
        }

        private addBuildings(gltf: any, map: google.maps.Map, onBuildingClicked: (building: Building) => void): RemoveListener {
            const buildingMeshes = gltf.scene.children[0].children as BuildingMesh[];
            const buildingUserData = buildingMeshes.map(mesh => mesh.userData);

            const cleanUp = [];
            for (let i = 0; i < buildingUserData.length; i++) {
                cleanUp.push(this.addBuilding(buildingUserData[i], map, onBuildingClicked));
            }

            return () => { cleanUp.forEach(c => c()); };
        }

        private addBuilding(userData: Building, map: google.maps.Map, onBuildingClicked: (building: Building) => void): RemoveListener {
            const polygonLimit = window.getTilesetLimit();
            const polygonCount = userData.polygon_count;
            const buildingHasTooManyPolygonsForSPT = polygonCount !== undefined && polygonCount > polygonLimit;
            if (buildingHasTooManyPolygonsForSPT) {
                const polygons: google.maps.Polygon[] = (userData.ground ?? []).map(p => new google.maps.Polygon({
                    paths: p,
                    geodesic: false,
                    strokeWeight: 1,
                    fillColor: '#7A7A7A',
                    fillOpacity: 0.6,
                    strokeColor: '#3F3F3F',
                    strokeOpacity: 0.7,
                    map: map
                })); 
                
                return () => {
                    polygons.forEach(p => p.setMap(null));
                }
            }
            else {
                const polygons: google.maps.Polygon[] = (userData.ground ?? []).map(p => new google.maps.Polygon({
                    paths: p,
                    geodesic: false,
                    strokeWeight: 1,
                    fillColor: '#81EB8A',
                    fillOpacity: 0.3,
                    strokeColor: '#12B012',
                    strokeOpacity: 0.7,
                    map: map
                }));

                const listeners: google.maps.MapsEventListener[] = [];
                for (const polygon of polygons) {
                    listeners.push(
                        google.maps.event.addListener(polygon, 'click', () => {
                            onBuildingClicked(userData)
                        }),
                        google.maps.event.addListener(polygon, 'mouseover', () => {
                            polygons.forEach(p => p.setOptions({ fillOpacity: 0.8, strokeOpacity: 0.9 }));
                        }),
                        google.maps.event.addListener(polygon, 'mouseout', () => {
                            polygons.forEach(p => p.setOptions({ fillOpacity: 0.3, strokeOpacity: 0.7 }));
                        })
                    );
                }

                return () => {
                    polygons.forEach(p => p.setMap(null));
                    listeners.forEach(l => l.remove());
                }
            }
        }
        
        private preventCaching() {
            return `prevent-cache=${(Math.random() * (1 << 31 - 1)) | 0}`;
        }
    }

    type uuid = string;
    type BuildingMesh = Omit<THREE.Mesh, 'userData'> & { userData: Building };

    type Building = {
        id: uuid;
        source: string;
        id_number: number | null;
        id_uuid: uuid | null;
        id_text: string | null;
        date_imported: string;
        date_improved: string;
        zmin: number;
        polygon_count: number;
        tile: string;
        ground: { lat: number, lng: number }[][] | null;
    }
    
    type Source = string;
    class SourceCounter {
        public constructor(credits: () => Record<string, string | undefined>) {
            this.credits = credits;
        }
        
        private credits: () => Record<string, string | undefined>;
        private counter = new Map<Source, number>();
        
        public add(sources: Source[]) {
            for (const source of sources) {
                this.counter.set(source, (this.counter.get(source) ?? 0) + 1);
            }
        }
        
        public substract(sources: Source[]) {
            for (const source of sources) {
                this.counter.set(source, (this.counter.get(source) ?? 1) - 1);
            }
        }
        
        public getActiveCredits(): string[] {
            const credits = this.credits();
            const activeCredits: string[] = [];
            
            for (const [source, count] of this.counter.entries()) {
                if (count > 0) {
                    activeCredits.push(credits[source] ?? 'No credits defined.');
                }
            }
            
            return activeCredits;
        }
    }
}

module MapDrawing {
    /**
     * Merges the two buildings and their roofs into a single building+roof.
     * If the two buildings are too far apart the second argument to this function is return;
     * The output only contains one building, if the two buildings do not touch each other one of the two is randomly selected.
     * @param oldShape already selected building
     * @param newShape building that should be appended to the first
     * @return a MapShape[] that can be imported into the MapDrawer
     */
    
    // Beyond this distance, selecting another building replaces the current selection.
    export const MERGE_REPLACE_THRESHOLD_KM = 0.5;

    /**
     * Returns true if the buildings are too far apart to be merged.
     * Defines the distance threshold used by mergeMapShapes.
     */
    export function wouldReplaceMapShapes(oldShape: SolarProTool.MapShape[], newShape: SolarProTool.MapShape[]): boolean {
        if (oldShape.length === 0 || newShape.length === 0){ 
            return false;
        }

        const b1 = oldShape.find(s => s.TypeName === window.BuildingTypeName);
        const b2 = newShape.find(s => s.TypeName === window.BuildingTypeName);
        if (!b1 || !b2) {
            return false;
        }

        const p1 = b1.borders[0].points[0];
        const p2 = b2.borders[0].points[0];

        return haversineDistanceKM(p1.latitude, p1.longitude, p2.latitude, p2.longitude) > MERGE_REPLACE_THRESHOLD_KM;
    }

    export function mergeMapShapes(oldShape: SolarProTool.MapShape[], newShape: SolarProTool.MapShape[]) {
        if (oldShape.length === 0) return newShape;
        if (newShape.length === 0) return oldShape;

        if (wouldReplaceMapShapes(oldShape, newShape)) {
            return newShape;
        }
        else {
            return mergeMapShapesInternal([...oldShape, ...newShape]);
        }
    }
    export function hasAnyIntersections(bounds: google.maps.LatLngBounds, borderJson: { lat: number, lng: number }[][][]) {
        const clf = 100000; // clipperLibFactor

        const clipPaths = borderJson.map(polygon => toClipperPolygon(polygon));

        const clipper = new ClipperLib.Clipper();

        clipper.AddPath(gmToClipperPolygon(bounds), ClipperLib.PolyType.ptSubject, true);

        for (let i = 0; i < clipPaths.length; i++) {
            clipper.AddPaths(clipPaths[i], ClipperLib.PolyType.ptClip, true);
        }

        const result = new ClipperLib.Paths();

        clipper.Execute(ClipperLib.ClipType.ctDifference, result, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);

        return result.length > 0;

        function gmToClipperPolygon(bounds: google.maps.LatLngBounds) {
            const n = bounds.getNorthEast().lat() * clf;
            const e = bounds.getNorthEast().lng() * clf;
            const s = bounds.getSouthWest().lat() * clf;
            const w = bounds.getSouthWest().lng() * clf;

            return [
                { X: e, Y: n },
                { X: w, Y: n },
                { X: w, Y: s },
                { X: e, Y: s }
            ]
        }

        function toClipperPolygon(borderPolygon: { lat: number, lng: number }[][]) {
            let result: { X: number, Y: number }[][] = [];

            for (let i = 0; i < borderPolygon.length; i++) {
                const ring = borderPolygon[i];

                result.push(ring.map(({ lat, lng }) => ({ X: lng * clf, Y: lat * clf })));
            }

            return result;
        }
    }
    function haversineDistanceKM(lat1Deg, lon1Deg, lat2Deg, lon2Deg) {
        function toRad(degree) {
            return degree * Math.PI / 180;
        }

        const lat1 = toRad(lat1Deg);
        const lon1 = toRad(lon1Deg);
        const lat2 = toRad(lat2Deg);
        const lon2 = toRad(lon2Deg);

        const { sin, cos, sqrt, atan2 } = Math;

        const R = 6371; // earth radius in km 
        const dLat = lat2 - lat1;
        const dLon = lon2 - lon1;
        const a = sin(dLat / 2) * sin(dLat / 2)
            + cos(lat1) * cos(lat2)
            * sin(dLon / 2) * sin(dLon / 2);
        const c = 2 * atan2(sqrt(a), sqrt(1 - a));
        const d = R * c;
        return d; // distance in km
    }
    
    function mergeMapShapesInternal(mapShapes: SolarProTool.MapShape[]) {
        const buildings = mapShapes.filter(shape => shape.TypeName === window.BuildingTypeName);
        const roofs = mapShapes.filter(shape => shape.TypeName === window.RoofTypeName);

        // Return every building and roof shape as-is:
        // - Buildings: each keeps its own IdString so the re-click early-return works per building.
        //   ClipperLib UNION can drop Z-values, making all result polygons inherit the first
        //   building's IdString — that would break the early-return check for every subsequent click.
        // - Roofs: each GIS face stays its own selectable surface; DBSCAN was merging faces across
        //   buildings into combined shapes ("Roof 00 & Roof 06"), breaking individual selection.
        return [...buildings, ...roofs];
    }

    function shouldMerge(a: SolarProTool.MapShape, b: SolarProTool.MapShape) {
        const heightError = 800; // in mm
        const haveSameHeight = Math.abs(a.borders[0].points[0].z - b.borders[0].points[0].z) < heightError;

        const orientationError = 0.02; // angle in radians
        const haveSameOrientation = a.mapSouthMarker?.Orientation === undefined || b.mapSouthMarker?.Orientation === undefined
            ? false
            : Math.abs(a.mapSouthMarker.Orientation - b.mapSouthMarker.Orientation) < orientationError;

        const slopeError = 1; // angle in degrees
        const haveSameSlope = Math.abs(a.borders[0].slope - b.borders[0].slope) < slopeError;

        const areBothFlatRoofs = a.borders[0].slope < 1 && b.borders[0].slope < 1;
        
        if (haveSameHeight
            && (areBothFlatRoofs || haveSameOrientation)
            && haveSameSlope) {
            return mapShapesOverlap(a, b);
        }
        else {
            return false;
        }
    }

    function mapShapesOverlap(a: SolarProTool.MapShape, b: SolarProTool.MapShape) {
        const paths = toClipperPolygons([a, b]);

        const clipper = new ClipperLib.Clipper();

        clipper.AddPath(paths[0], ClipperLib.PolyType.ptSubject, true);
        clipper.AddPath(paths[1], ClipperLib.PolyType.ptClip, true);

        const result = new ClipperLib.Paths();

        clipper.Execute(ClipperLib.ClipType.ctUnion, result, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);

        return result.length === 1;
    }
    
    function labelsToArrayArray<T>(elements: T[], labels: number[]) {
        const labelCount = labels.length === 0 ? 1 : labels.reduce((p, c) => Math.max(p, c)) + 1;

        const result: T[][] = [];

        for (let i = 0; i < labelCount; i++) {
            const labelGroup: T[] = [];

            for (let j = 0; j < elements.length; j++) {
                if (labels[j] === i) {
                    labelGroup.push(elements[j]);
                }
            }

            result[i] = labelGroup;
        }

        return result;
    }

    function _DBSCAN<T>(elements: T[], isNeighbor: (a: T, b: T) => boolean, minPoints: number = 2): number[] {
        let clusterCounter = 0;
        const labels: (number | undefined)[] = new Array(elements.length);

        for (let i = 0; i < elements.length; i++) {
            if (labels[i] !== undefined) continue;

            const neighborIndices = rangeQuery(elements[i]);

            if (neighborIndices.length < minPoints - 1) {
                labels[i] = 0;
                continue;
            }

            clusterCounter++;
            labels[i] = clusterCounter;

            for (let j = 0; j < neighborIndices.length; j++) {
                const neighborIndex = neighborIndices[j];

                if (labels[neighborIndex] === 0) labels[neighborIndex] = clusterCounter;
                if (labels[neighborIndex] !== undefined) continue;

                labels[neighborIndex] = clusterCounter;

                const newNeighbors = rangeQuery(elements[neighborIndex]);

                if (newNeighbors.length >= minPoints - 1) {
                    neighborIndices.push(...newNeighbors);
                }
            }
        }

        return labels.map(v => v ?? (() => { throw new Error('Error in DBSCAN algorithm, label is missing.') })());

        function rangeQuery(center: T) {
            const neigbors: number[] = [];

            for (let i = 0; i < elements.length; i++) {
                if (center !== elements[i] && isNeighbor(center, elements[i])) {
                    neigbors.push(i);
                }
            }

            return neigbors;
        }
    }

    function mergePolygons(mapShapes: SolarProTool.MapShape[]): SolarProTool.MapShape[] {
        const paths = toClipperPolygons(mapShapes);
        
        const clipper = new ClipperLib.Clipper();
        
        clipper.AddPath(paths[0], ClipperLib.PolyType.ptSubject, true);
        for (let i = 1; i < paths.length; i++) {
            clipper.AddPath(paths[i], ClipperLib.PolyType.ptClip, true);
        }
        
        const result = new ClipperLib.Paths();
        
        clipper.Execute(ClipperLib.ClipType.ctUnion, result, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);
        
        return backToMapShapes(result, mapShapes);
    }
    
    const clf = 100000; // clipperLibFactor
    
    function backToMapShapes(newPaths: ClipperLib.Paths, mapShapes: SolarProTool.MapShape[]) {
        const result: SolarProTool.MapShape[] = [];
        
        for (const polygon of newPaths) {
            const mapShapesOfPolygon = [...new Set(polygon.map(point => point.Z))].map(i => mapShapes[i]);
            const meanHeightZ = mapShapesOfPolygon.reduce((a, b) => a + b.borders[0].points[0].z, 0) / mapShapesOfPolygon.length;
            const combinedMapShape = combineMapShapes(mapShapesOfPolygon, polygon.map(point => ({ longitude: point.X / clf, latitude: point.Y / clf, altitude: 0, x: 0, y: 0, z: meanHeightZ })));
            result.push(combinedMapShape);
        }
        
        return result;
    }
    
    function toClipperPolygons(mapShapes: SolarProTool.MapShape[]) {
        let result: { X: number, Y: number, Z: number }[][] = [];
        
        for (let i = 0; i < mapShapes.length; i++) {
            const mapShape = mapShapes[i];
            
            result.push(mapShape.borders[0].points.map((point) => ({ X: point.longitude * clf, Y: point.latitude * clf, Z: i })));
        }
        
        return result;
    }

    function combineMapShapes(mapShapes: SolarProTool.MapShape[], newMapCoord: SolarProTool.MapCoord[]) {
        let result: SolarProTool.MapShape = {
            IdString: null,
            IsParallel: false,
            TypeName: window.RoofTypeName,
            RotationalAngle: 0,
            Title: null,
            borders: [{
                points: newMapCoord,
                isClockwise: false,
                minLat: Number.MAX_VALUE,
                minLng: Number.MAX_VALUE,
                maxLat: -Number.MAX_VALUE,
                maxLng: -Number.MAX_VALUE,
                minx: 0,
                miny: 0,
                maxx: 0,
                maxy: 0,
                width: 0,
                height: 0,
                slope: 0,
                useProjection: true
            }],
            mapSouthMarker: null,
            IsActive: true,
            SlopeOverride: true,
            separationPolylines: [],
            cuttingPolylines: [],
            CustomHeights: [],
            UseCustomSouthMarker: true,
            LeaveRoofAreaUntouched: true,
            UseSimpleRidgeTool: false
        };

        for (const mapShape of mapShapes) {
            const resultBorder = result.borders[0];
            const mapShapeBorder = mapShape.borders[0];
            result = {
                IdString: result.IdString ?? mapShape.IdString,
                IsParallel: result.IsParallel || mapShape.IsParallel,
                TypeName: mapShape.TypeName,
                RotationalAngle: mapShape.RotationalAngle,
                Title: result.Title === null || mapShape.Title === null ? (result.Title ?? mapShape.Title) : result.Title + ' & ' + mapShape.Title,
                borders: [{
                    points: newMapCoord,
                    isClockwise: false,
                    minLat: Math.min(resultBorder.minLat, mapShapeBorder.minLat),
                    minLng: Math.min(resultBorder.minLng, mapShapeBorder.minLng),
                    maxLat: Math.max(resultBorder.maxLat, mapShapeBorder.maxLat),
                    maxLng: Math.max(resultBorder.maxLng, mapShapeBorder.maxLng),
                    minx: 0,
                    miny: 0,
                    maxx: 0,
                    maxy: 0,
                    width: 0,
                    height: 0,
                    slope: (resultBorder.slope + mapShapeBorder.slope) / 2,
                    useProjection: true
                }],
                mapSouthMarker:  chooseMapSouthMarker(result.mapSouthMarker, mapShape.mapSouthMarker, newMapCoord),
                IsActive: true,
                SlopeOverride: true,
                separationPolylines: [],
                cuttingPolylines: [],
                CustomHeights: [],
                UseCustomSouthMarker: true,
                LeaveRoofAreaUntouched: true,
                UseSimpleRidgeTool: false
            };
        }

        return result;
    }
    
    function chooseMapSouthMarker(a: SolarProTool.LatLngOrientation | undefined | null, b: SolarProTool.LatLngOrientation | undefined | null, mapCoords: SolarProTool.MapCoord[]) {
        if (a === undefined || a === null) return b;
        if (b === undefined || b === null) return a;
        
        let result = a;
        let closest = Number.MAX_VALUE;
        
        for (let i = 0; i < mapCoords.length; i++) {
            const p1 = mapCoords[i];
            const p2 = mapCoords[(i + 1) % mapCoords.length];
            
            const lon = (p1.longitude + p2.longitude) / 2;
            const lat = (p1.latitude + p2.latitude) / 2;
            
            const distanceToA = square(a.Longitude - lon) + square(a.Latitude - lat);
            if (distanceToA < closest) {
                closest = distanceToA;
                result = a;
            }
            
            const distanceToB = square(b.Longitude - lon) + square(b.Latitude - lat);
            if (distanceToB < closest) {
                closest = distanceToB;
                result = b;
            }
        }
        
        return result;

        function square(x: number) {
            return x * x;
        }
    }
}